import React, { useState } from 'react';
import * as MuiIcons from '@mui/icons-material';
import { getNestedValue, formatString, getColorClasses } from './utils';

/**
 * ImageCardWidget - Renders an image card or file attachment preview
 * 
 * Config (from config_json):
 * {
 *   "type": "image_card",
 *   "options": {
 *     "title": "설비 사진",
 *     "imageUrl": "/images/machines/{machine_manage_code}.jpg",  // Supports format strings
 *     "fallbackIcon": "CameraAlt",         // MUI icon shown when image fails
 *     "iconColor": "indigo",               // Fallback icon color
 *     "height": 160,                        // Image height in pixels
 *     "objectFit": "cover",                 // cover | contain
 *     "caption": "최근 점검 시 촬영",        // Optional caption below image
 *     "actionType": "openImage",            // Optional action on click
 *     "dataKey": "machine_manage_code"      // Data passed to action handler
 *   },
 *   "items": []
 * }
 * 
 * --- Multi-image mode (uses items array) ---
 * {
 *   "type": "image_card",
 *   "options": {
 *     "title": "설비 사진",
 *     "height": 120,
 *     "columns": 2
 *   },
 *   "items": [
 *     { "label": "전면", "dataKey": "image_front_url", "fallbackIcon": "CameraAlt" },
 *     { "label": "후면", "dataKey": "image_back_url", "fallbackIcon": "CameraAlt" }
 *   ]
 * }
 */

/**
 * Single image card component
 */
const SingleImage = ({ src, title, height, objectFit, fallbackIcon, iconColor, caption, onClick }) => {
  const [hasError, setHasError] = useState(false);
  const FallbackIcon = MuiIcons[fallbackIcon] || MuiIcons.CameraAlt;
  const colors = getColorClasses(iconColor || 'indigo');

  return (
    <div
      className={`rounded-2xl overflow-hidden bg-white/5 ${onClick ? 'cursor-pointer hover:brightness-110 active:scale-[0.98]' : ''} transition-all`}
      style={{ border: '1px solid rgba(255,255,255,0.06)' }}
      onClick={onClick}
    >
      {!hasError && src ? (
        <div className="relative" style={{ height: `${height}px` }}>
          <img
            src={src}
            alt={title || ''}
            className="w-full h-full"
            style={{ objectFit: objectFit || 'cover' }}
            onError={() => setHasError(true)}
          />
          {/* Gradient overlay at bottom for caption readability */}
          {caption && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent pt-6 pb-2 px-3">
              <span className="text-[10px] text-white/80 font-medium">{caption}</span>
            </div>
          )}
        </div>
      ) : (
        <div
          className="flex flex-col items-center justify-center gap-2"
          style={{ height: `${height}px` }}
        >
          <div className={`p-3 rounded-2xl ${colors.bgLight}`}>
            <FallbackIcon sx={{ fontSize: 28 }} className={colors.text} />
          </div>
          <span className="text-[10px] text-slate-600 font-bold">이미지 없음</span>
        </div>
      )}

      {/* Title bar below image (when no gradient caption) */}
      {title && !caption && (
        <div className="px-3 py-2" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          <span className="text-[11px] text-slate-300 font-bold">{title}</span>
        </div>
      )}
    </div>
  );
};

export default function ImageCardWidget({ config, data, onAction }) {
  if (!config) return null;

  const items = config.items || [];
  const height = config.height || 160;
  const columns = config.columns || 1;

  // Resolve image URL with format string support
  const resolveUrl = (url) => {
    if (!url) return null;
    if (url.includes('{')) return formatString(url, data);
    return getNestedValue(data, url, null) || url;
  };

  const handleClick = (actionType, actionData) => {
    if (onAction && actionType) {
      onAction(actionType, actionData);
    }
  };

  // Single image mode (no items, config-level imageUrl)
  if (items.length === 0 && config.imageUrl) {
    const imageUrl = resolveUrl(config.imageUrl);
    const actionData = config.dataKey ? getNestedValue(data, config.dataKey) : null;

    return (
      <div>
        {config.title && (
          <div className="px-1 mb-2">
            <span className="text-[12px] text-slate-400 font-bold">{config.title}</span>
          </div>
        )}
        <SingleImage
          src={imageUrl}
          title={null}
          height={height}
          objectFit={config.objectFit}
          fallbackIcon={config.fallbackIcon}
          iconColor={config.iconColor}
          caption={config.caption}
          onClick={config.actionType ? () => handleClick(config.actionType, actionData) : undefined}
        />
      </div>
    );
  }

  // Multi-image mode (items array)
  if (items.length > 0) {
    const gridClass = {
      1: 'grid-cols-1',
      2: 'grid-cols-2',
      3: 'grid-cols-3',
    }[columns] || 'grid-cols-2';

    return (
      <div>
        {config.title && (
          <div className="px-1 mb-2">
            <span className="text-[12px] text-slate-400 font-bold">{config.title}</span>
          </div>
        )}
        <div className={`grid ${gridClass} gap-2`}>
          {items.map((item, idx) => {
            const imgUrl = resolveUrl(item.dataKey);
            return (
              <SingleImage
                key={idx}
                src={imgUrl}
                title={item.label}
                height={height}
                objectFit={config.objectFit}
                fallbackIcon={item.fallbackIcon || config.fallbackIcon}
                iconColor={item.iconColor || config.iconColor}
                caption={null}
                onClick={item.actionType ? () => handleClick(item.actionType, imgUrl) : undefined}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // No imageUrl and no items — render placeholder
  return (
    <div>
      {config.title && (
        <div className="px-1 mb-2">
          <span className="text-[12px] text-slate-400 font-bold">{config.title}</span>
        </div>
      )}
      <SingleImage
        src={null}
        title={null}
        height={height}
        fallbackIcon={config.fallbackIcon}
        iconColor={config.iconColor}
      />
    </div>
  );
}
